import type { ListItem } from '../../../../components/Toolkit/Inputs/CustomSelect';
import type { Options, SerializedMakeModel } from './MakeModel.typed';
import { FEATURE_TRIMS_FILTER } from '../../../../utils';
import { ALL_MAKES, ALL_MODELS, ALL_TRIMS } from './MakeModel.constants';

const mapListItemToOption = (items?: Array<ListItem>): Options['items'] => {
  return (
    items
      ?.filter(({ value }) => value !== '')
      .map(({ displayName, value, count }) => ({
        displayName,
        value,
        count,
      })) || []
  );
};

const mapMakeOptions = (
  all?: Array<ListItem>,
  popular?: Array<ListItem>,
): Options[] => {
  const allOptions: Options = {
    title: ALL_MAKES,
    items: mapListItemToOption(all),
  };

  const popularOptions: Options = {
    title: 'Popular',
    items: mapListItemToOption(popular),
  };

  const options: Options[] = [
    { title: null, items: [{ value: '', displayName: ALL_MAKES }] },
    popularOptions,
    allOptions,
  ];

  return options;
};

const mapDisabledOptions = (
  options: Array<Options>,
  selectedMakes?: Array<string>,
): Array<Options> => {
  return options.map((option) => {
    return {
      ...option,
      items: option.items.map((item) => ({
        value: item.value ?? '',
        displayName: item.displayName ?? '',
        isDisabled:
          selectedMakes && item.value
            ? selectedMakes?.includes(item.value)
            : false,
        count: item.count,
      })),
    };
  });
};

const mapDisabledModelOptions = (
  options: Array<Options>,
  disabledModels?: Array<string>,
): Array<Options> => {
  return options.map((option) => {
    return {
      ...option,
      items: option.items.map((item) => ({
        value: item.value ?? '',
        displayName: item.displayName ?? '',
        isDisabled:
          disabledModels && item.value && item.value !== ''
            ? disabledModels?.includes(item.value)
            : false,
        count: item.count,
      })),
    };
  });
};

const mapModelOptions = (
  all?: Array<ListItem>,
  popular?: Array<ListItem>,
): Options[] => {
  const allOptions: Options = {
    title: ALL_MODELS,
    items: mapListItemToOption(all),
  };

  const popularItems = mapListItemToOption(popular);

  const popularOptions: Options = {
    title: popularItems.length !== 0 ? 'Popular' : null,
    items: mapListItemToOption(popular),
  };

  const options: Options[] = [
    { title: null, items: [{ value: '', displayName: ALL_MODELS }] },
    popularOptions,
    allOptions,
  ];

  return options;
};

const mapListItemToTrimOption = (items?: Array<ListItem>): Options['items'] => {
  return (
    items
      ?.filter(({ value }) => value !== '')
      .map(({ displayName, value, count }) => ({
        displayName: decodeURIComponent(displayName),
        value: decodeURIComponent(value),
        count,
      })) || []
  );
};

const mapTrimOptions = (
  all?: Array<ListItem>,
  popular?: Array<ListItem>,
): Options[] => {
  const allOptions: Options = {
    title: ALL_TRIMS,
    items: mapListItemToTrimOption(all),
  };

  const popularItems = mapListItemToTrimOption(popular);

  const popularOptions: Options = {
    title: popularItems.length !== 0 ? 'Popular' : null,
    items: mapListItemToTrimOption(popular),
  };

  const options: Options[] = [
    { title: null, items: [{ value: '', displayName: ALL_TRIMS }] },
    popularOptions,
    allOptions,
  ];

  return options;
};

const mapMakeModelQuery = (makeModelValue: string, isEditable?: boolean) => {
  const [make, model, trim] = makeModelValue.split(';');
  const modelValue = model ? model.split('model:') : [];
  const trimValue = trim && FEATURE_TRIMS_FILTER ? trim.split('trim:') : [];

  const mappedMakeAndModels = {
    make,
    model: modelValue[1]?.split(','),
    trim: FEATURE_TRIMS_FILTER
      ? trimValue[1]?.split(',').map((t) => decodeURIComponent(t))
      : undefined,
    isEditable,
  };

  return mappedMakeAndModels;
};

/*
 *  We may map make model values on the server. In this case, we pass type-compliant
 *  null values rather than undefined
 */

const mapServerMakeModels = (
  make?: string,
  model?: string,
  trim?: Array<string>,
  makeQuery?: string | Array<string> | null,
): Array<SerializedMakeModel> | undefined => {
  if (make) {
    if (model) {
      return [{ make, model: [model], trim: trim ?? null }];
    }

    return [{ make, model: null, trim: null }];
  }
  if (Array.isArray(makeQuery)) {
    return makeQuery.map((item) => {
      const mappedQuery = mapMakeModelQuery(item, false);
      return {
        ...mappedQuery,
        model: mappedQuery.model ?? null,
        trim: FEATURE_TRIMS_FILTER ? mappedQuery.trim ?? null : null,
      };
    });
  }
  if (typeof makeQuery === 'string') {
    const mappedQuery = mapMakeModelQuery(makeQuery, false);
    return mappedQuery
      ? [
          {
            ...mappedQuery,
            model: mappedQuery.model ?? null,
            trim: mappedQuery.trim ?? null,
          },
        ]
      : undefined;
  }
  return undefined;
};

export {
  mapMakeOptions,
  mapDisabledOptions,
  mapDisabledModelOptions,
  mapModelOptions,
  mapTrimOptions,
  mapMakeModelQuery,
  mapServerMakeModels,
};
